import { useEffect, useState, useCallback, useRef } from 'react';
import { listTrending, trendingCounts, pushTrending, skipTrending, getCode, setCode, clearCode } from '../api/lionjob';
import LionjobPager from '../components/LionjobPager';

// Màn TRENDING (Lionjob) — đầu phễu: sản phẩm đang bán chạy quét từ matrixty
// (HeyEtsy = spy có số liệu bán thật; 4 store đối thủ chỉ có thứ hạng).
// Chọn hàng tiềm năng -> "Đẩy sang Scraped Products" để làm SEO.
const SOURCES = [
  { id: 'heyetsy_trending', label: 'HeyEtsy', kind: 'spy' },
  { id: 'wrappiness_bestsellers', label: 'Wrappiness', kind: 'store' },
  { id: 'wanderprints_trending', label: 'Wanderprints', kind: 'store' },
  { id: 'macorner_bestsellers', label: 'Macorner', kind: 'store' },
  { id: 'pawfecthouse_trending', label: 'Pawfecthouse', kind: 'store' },
];
const BOARDS = [
  ['trending', 'Trending'], ['hot', 'Hot'], ['best-seller', 'Best Seller'],
  ['popular-now', 'Popular Now'], ['ads', "Etsy's Pick"],
];
const SORTS = [
  ['newest', 'Mới xuất hiện'], ['score', 'Điểm cơ hội cao nhất'],
  ['sold', 'Bán 24h cao nhất'], ['velocity', 'Tốc độ bán cao nhất'], ['fresh', 'Mới đăng nhất'],
];

const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('vi-VN').format(n));

export default function TrendingPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [lockErr, setLockErr] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [counts, setCounts] = useState(null);
  const [page, setPage] = useState(1);
  const [source, setSource] = useState('heyetsy_trending');
  const [board, setBoard] = useState('trending');
  const [status, setStatus] = useState('new');
  const [sort, setSort] = useState('newest');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyPod, setOnlyPod] = useState(true);
  const [hideIp, setHideIp] = useState(true);
  const [minPrice, setMinPrice] = useState(false);
  const [smallShop, setSmallShop] = useState(false);
  const [sel, setSel] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [extReady, setExtReady] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    const params = { page, limit: 24, sort, source, board, status };
    if (q) params.q = q;
    if (from) params.from = from;
    if (to) params.to = to;
    if (onlyPod) params.onlyPod = '1';
    if (hideIp) params.hideIp = '1';
    if (minPrice) params.minPrice = 10;
    if (smallShop) params.smallShop = '1';
    try {
      const data = await listTrending(params);
      setRows(data.rows); setMeta({ page: data.page, totalPages: data.totalPages, total: data.total });
      setUnlocked(true);
      trendingCounts().then(setCounts).catch(() => {});
    } catch (e) {
      const m = e.response?.data?.message || e.message || 'Không kết nối được backend';
      if (e.response?.status === 403) { clearCode(); setUnlocked(false); }
      setLockErr(m); setMsg('Lỗi: ' + m);
    }
  }, [page, sort, source, board, status, q, from, to, onlyPod, hideIp, minPrice, smallShop]);

  useEffect(() => { if (unlocked) load(); }, [load, unlocked]);
  useEffect(() => { if (getCode()) load(); /* eslint-disable-next-line */ }, []);

  // Cầu nối extension 🦁 — quét màn Trending của matrixty.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window || !ev.data || ev.data.source !== 'lionjob-ext') return;
      if (ev.data.type === 'ready') setExtReady(true);
      if (ev.data.type === 'scan-result') {
        setScanning(false);
        const r = ev.data.resp;
        if (r && r.ok) { setMsg(`✓ Quét ${r.scanned ?? r.received} sản phẩm · mới ${r.inserted}, cập nhật ${r.updated}`); loadRef.current?.(); }
        else setMsg('✗ ' + ((r && r.error) || 'Quét lỗi'));
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'lionjob-page', type: 'ping' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  function scan() {
    if (!extReady) { setMsg('Chưa thấy extension 🦁 — cài extension rồi mở tab matrixty.com.'); return; }
    setScanning(true); setMsg('Đang quét Trending từ Matrixty…');
    window.postMessage({ source: 'lionjob-page', type: 'scan', kind: 'trending' }, '*');
    setTimeout(() => setScanning((s) => { if (s) setMsg('Quét quá lâu — kiểm tra tab matrixty.com.'); return false; }), 60000);
  }

  async function unlock(e) {
    e?.preventDefault();
    const c = codeInput.trim(); if (!c) { setLockErr('Nhập mã code'); return; }
    setLockErr(''); setCode(c); setUnlocking(true);
    try { await load(); } finally { setUnlocking(false); }
  }

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectPage = () => setSel(rows.map((r) => r.id));
  const clearSel = () => setSel([]);

  async function doPush(force = false) {
    if (!sel.length) return;
    setBusy(true);
    try {
      const r = await pushTrending(sel, force);
      // Hàng có cảnh báo được giữ lại -> hỏi lại, không tự đẩy (theo thiết kế đã duyệt).
      if (r.blocked?.length && !force) {
        const names = r.blocked.map((b) => `• ${b.title.slice(0, 60)} — ${b.reason}`).join('\n');
        if (window.confirm(`${r.blocked.length} sản phẩm có cảnh báo:\n\n${names}\n\nVẫn đẩy sang Scraped Products?`)) {
          const r2 = await pushTrending(r.blocked.map((b) => b.id), true);
          setMsg(`✓ ${r.message} · ${r2.message}`);
        } else setMsg('✓ ' + r.message);
      } else setMsg('✓ ' + r.message);
      clearSel(); load();
    } catch (e) { setMsg('Lỗi đẩy: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(false); }
  }
  async function doSkip() {
    if (!sel.length) return;
    setBusy(true);
    try { const r = await skipTrending(sel); setMsg('✓ ' + r.message); clearSel(); load(); }
    catch (e) { setMsg('Lỗi: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(false); }
  }

  if (!unlocked) {
    return (
      <div className="tr-root"><style>{CSS}</style>
        <div className="tr-lock"><div className="tr-lockCard">
          <div className="tr-lockIco">🦁</div><h2>Trending</h2>
          <p className="tr-lockSub">Khu vực chỉ dành cho Giám đốc. Nhập mã code để mở.</p>
          <form onSubmit={unlock}>
            <input className="tr-lockInput" type="password" autoFocus placeholder="Mã code" disabled={unlocking}
              value={codeInput} onChange={(e) => setCodeInput(e.target.value)} />
            <button className="tr-btn primary" type="submit" disabled={unlocking} style={{ width: '100%', marginTop: 10 }}>
              {unlocking ? '⏳ Đang kết nối…' : 'Mở khóa'}</button>
          </form>
          {lockErr && <div className="tr-lockErr">⚠ {lockErr}</div>}
        </div></div>
      </div>
    );
  }

  const bySource = counts?.bySource || {};
  const byBoard = counts?.byBoard || {};

  return (
    <div className="tr-root"><style>{CSS}</style>
      <div className="tr-head">
        <div>
          <h2>Trending</h2>
          <div className="tr-sub">{msg || 'Sản phẩm đang bán chạy, quét từ HeyEtsy và 4 store đối thủ. Chọn hàng tiềm năng rồi đẩy sang Scraped Products.'}</div>
        </div>
        <div className="tr-sp" />
        <button className="tr-btn primary" onClick={scan} disabled={scanning || !extReady}
          title={extReady ? 'Quét màn Trending từ matrixty.com' : 'Cần cài extension 🦁'}>
          {scanning ? '⏳ Đang quét…' : '🦁 Quét Trending'}
        </button>
      </div>

      {/* Dải tóm tắt: tổng quan trước chi tiết */}
      <div className="tr-strip">
        <Tile k="Đang xem" v={fmt(meta.total)} n={`${SOURCES.find((s) => s.id === source)?.label || source} · bảng ${BOARDS.find((b) => b[0] === board)?.[1] || board}`} />
        <Tile k="Mới hôm nay" v={fmt(counts?.newToday)} n="chưa từng xuất hiện" />
        <Tile k="Chưa xử lý" v={fmt(counts?.unprocessed)} n="còn lại để duyệt" />
        <Tile k="Cần loại" v={fmt(counts?.risky)} n="nghi bản quyền hoặc không làm được POD" flag />
        <Tile k="Dữ liệu lúc" v={counts?.lastScrapeAt ? new Date(counts.lastScrapeAt).toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' }) : '—'}
          n={counts?.lastScrapeAt ? new Date(counts.lastScrapeAt).toLocaleDateString('vi') : 'chưa quét'} />
      </div>

      <div className="tr-filters">
        <div className="tr-srcbar">
          {SOURCES.map((s) => (
            <button key={s.id} className={'tr-src' + (source === s.id ? ' on' : '')}
              onClick={() => { setSource(s.id); setPage(1); clearSel(); }}>
              {s.label} <span className="tr-kind">{s.kind}</span>
              <span className="tr-c">{fmt(bySource[s.id] || 0)}</span>
            </button>
          ))}
        </div>

        <div className="tr-row">
          <span className="tr-lab">Bảng</span>
          {BOARDS.map(([v, label]) => (
            <button key={v} className={'tr-pill' + (board === v ? ' on' : '')}
              onClick={() => { setBoard(v); setPage(1); clearSel(); }}>{label} <span className="c">{fmt(byBoard[v] || 0)}</span></button>
          ))}
        </div>

        <div className="tr-row">
          <input className="tr-fld" style={{ minWidth: 190 }} placeholder="Tìm theo tên hoặc link…"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <select className="tr-fld" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="all">Tất cả trạng thái</option>
            <option value="new">Chưa xử lý</option>
            <option value="pushed">Đã đẩy</option>
            <option value="skipped">Đã bỏ qua</option>
          </select>
          <select className="tr-fld" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="tr-fld" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          <input className="tr-fld" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          <label className="tr-chk"><input type="checkbox" checked={sel.length > 0 && sel.length === rows.length}
            onChange={(e) => (e.target.checked ? selectPage() : clearSel())} /> Chọn cả trang</label>
        </div>

        <div className="tr-row">
          <span className="tr-lab">Bộ lọc riêng</span>
          <button className={'tr-pill' + (onlyPod ? ' on' : '')} onClick={() => { setOnlyPod((v) => !v); setPage(1); }}>Chỉ hàng làm được POD</button>
          <button className={'tr-pill' + (hideIp ? ' on' : '')} onClick={() => { setHideIp((v) => !v); setPage(1); }}>Ẩn hàng nghi bản quyền</button>
          <button className={'tr-pill' + (minPrice ? ' on' : '')} onClick={() => { setMinPrice((v) => !v); setPage(1); }}>Giá ≥ 10 USD</button>
          <button className={'tr-pill' + (smallShop ? ' on' : '')} onClick={() => { setSmallShop((v) => !v); setPage(1); }}>Shop nhỏ (dễ chen)</button>
        </div>
      </div>

      <div className="tr-grid">
        {rows.length === 0 && <div className="tr-empty">Chưa có dữ liệu. Bấm <b>🦁 Quét Trending</b> để lấy từ matrixty.com.</div>}
        {rows.map((r) => <Card key={r.id} r={r} sel={sel.includes(r.id)} onPick={() => toggle(r.id)} />)}
      </div>

      <LionjobPager page={meta.page} totalPages={meta.totalPages} onGo={setPage} />

      {sel.length > 0 && (
        <div className="tr-bar">
          <span className="tr-n">Đã chọn <span>{sel.length}</span></span>
          <button className="tr-btn primary" onClick={() => doPush(false)} disabled={busy}>
            {busy ? '⏳ Đang đẩy…' : 'Đẩy sang Scraped Products'}</button>
          <button className="tr-btn" onClick={doSkip} disabled={busy}>Đánh dấu đã xem</button>
          <button className="tr-link" onClick={clearSel}>Bỏ chọn</button>
          <span className="tr-note">Hàng có cảnh báo sẽ được hỏi lại trước khi đẩy.</span>
        </div>
      )}
    </div>
  );
}

function Tile({ k, v, n, flag }) {
  return (
    <div className={'tr-tile' + (flag ? ' flag' : '')}>
      <div className="tr-tk">{k}</div><div className="tr-tv">{v}</div><div className="tr-tn">{n}</div>
    </div>
  );
}

function Card({ r, sel, onPick }) {
  const risk = r.riskIp ? 'ip' : r.riskNonPod ? 'nonpod' : '';
  const isSpy = r.sourceKind === 'spy';
  const noData = isSpy && !r.sold24h && !r.views24h;
  return (
    <article className={'tr-card' + (sel ? ' sel' : '') + (risk ? ' risk-' + risk : '')}
      onClick={onPick} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(); } }}>
      <div className="tr-shot">
        {r.image ? <img src={r.image} alt="" loading="lazy" /> : <div className="tr-noimg" />}
        <div className="tr-box">{sel ? '✓' : ''}</div>
        {r.daysOld <= 2 && r.daysOld > 0
          ? <div className="tr-fresh">Mới {r.daysOld}n</div>
          : r.scrapeCount > 1 ? <div className={'tr-heat' + (r.scrapeCount >= 20 ? ' hi' : '')}>{r.scrapeCount}×</div> : null}
        {r.position > 0 && <div className="tr-rank">#{r.position}</div>}
      </div>
      <div className="tr-body">
        <div className="tr-toprow">
          <span className={'tr-srctag' + (isSpy ? '' : ' store')}>{(SOURCES.find((s) => s.id === r.source)?.label) || r.source}</span>
          <span className="tr-price">{r.price ? `${r.price} ${r.currency === 'USD' ? '$' : r.currency}` : '—'}</span>
        </div>
        <div className="tr-title">
          <a href={r.etsyUrl || r.productUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{r.title || '(không tên)'}</a>
        </div>

        {isSpy && !noData ? (
          <div className="tr-mrow">
            <M k="Bán 24h" v={r.sold24h} good={r.sold24h > 0} />
            <M k="Xem 24h" v={r.views24h} />
            <M k="Thích" v={r.favoritesRatePct ? r.favoritesRatePct + '%' : '—'} good={r.favoritesRatePct >= 10} />
          </div>
        ) : (
          <div className="tr-mrow"><div className="tr-m none">
            {isSpy ? `Mới ${r.daysOld}n — chưa đủ dữ liệu để chấm` : 'Store đối thủ — không công bố lượt bán / lượt xem'}
          </div></div>
        )}

        <div className={'tr-derived' + (isSpy ? '' : ' plain')}>
          {isSpy ? (
            <>
              <div className="tr-d"><div className="tr-dk">Tốc độ bán</div><div className="tr-dv">{r.salesVelocity != null ? r.salesVelocity : '—'} <small>/ngày</small></div></div>
              <div className="tr-d"><div className="tr-dk">Chuyển đổi</div><div className="tr-dv">{r.conversionRate != null ? r.conversionRate + '%' : '—'}</div></div>
            </>
          ) : (
            <>
              <div className="tr-d"><div className="tr-dk">Xếp hạng nội bộ</div><div className="tr-dv">#{r.position || '—'} <small>{r.board}</small></div></div>
              <div className="tr-d"><div className="tr-dk">Trụ hạng</div><div className="tr-dv">{r.scrapeCount || 0}<small>n</small></div></div>
            </>
          )}
          <div className="tr-score">
            <div className="tr-dk">Điểm</div>
            <div className={'tr-dv big' + (r.opportunityScore == null ? ' off' : '')}>{r.opportunityScore == null ? '—' : r.opportunityScore}</div>
          </div>
        </div>

        <div className="tr-foot">
          <span className="tr-shop">{r.shopName || '—'}{r.shopTotalSold ? ` · ${fmt(r.shopTotalSold)} đã bán` : ''}</span>
          <span className="tr-age">{r.daysOld ? r.daysOld + 'n' : ''}</span>
        </div>
        <div className="tr-chips">
          {r.riskIp && <span className="tr-chip dang">Nghi bản quyền{r.ipMatch?.[0] ? ' · ' + r.ipMatch[0] : ''}</span>}
          {r.riskNonPod && <span className="tr-chip warn">Hàng digital, không in được</span>}
          {!r.riskNonPod && r.podKeywords?.length > 0 && <span className="tr-chip ok">POD · {r.podKeywords[0]}</span>}
          {r.riskFlags?.includes('pod_unknown') && !r.riskNonPod && <span className="tr-chip mute">Chưa rõ có in được không</span>}
          {r.riskFlags?.includes('low_price') && <span className="tr-chip warn">Giá thấp</span>}
          {isSpy && r.views24h > 30 && r.sold24h === 0 && <span className="tr-chip warn">Xem nhiều, chưa bán</span>}
          {r.shopTotalSold > 0 && r.shopTotalSold < 500 && <span className="tr-chip ok">Shop nhỏ</span>}
          {r.status === 'pushed' && <span className="tr-chip info">Đã đẩy</span>}
          {r.status === 'skipped' && <span className="tr-chip mute">Đã bỏ qua</span>}
          {r.status === 'new' && <span className="tr-chip mute">Chưa xử lý</span>}
        </div>
      </div>
    </article>
  );
}

function M({ k, v, good }) {
  return <div className={'tr-m' + (good ? ' good' : '')}><div className="tr-mk">{k}</div><div className="tr-mv">{v ?? '—'}</div></div>;
}

const CSS = `
.tr-root{color:var(--text);padding-bottom:70px}
.tr-head{display:flex;gap:12px;align-items:flex-start;margin-bottom:16px;flex-wrap:wrap}
.tr-head h2{margin:0;font-size:20px}
.tr-sub{color:var(--muted);font-size:12.5px;margin-top:3px;max-width:70ch}
.tr-sp{flex:1}
.tr-btn{background:var(--panel);color:var(--text);border:1px solid var(--border);padding:7px 13px;border-radius:9px;cursor:pointer;font-size:13px}
.tr-btn:hover:not(:disabled){border-color:var(--accent)}
.tr-btn:disabled{opacity:.45;cursor:not-allowed}
.tr-btn.primary{background:var(--accent);border-color:var(--accent);color:#fff;font-weight:600}
.tr-link{border:0;background:none;color:var(--muted);text-decoration:underline;cursor:pointer;font-size:13px;font:inherit;padding:6px 4px}
.tr-link:hover{color:var(--text)}

.tr-strip{display:grid;grid-template-columns:repeat(auto-fit,minmax(148px,1fr));gap:10px;margin-bottom:16px}
.tr-tile{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:11px 13px}
.tr-tk{font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:var(--muted);font-weight:600}
.tr-tv{font-size:21px;font-weight:600;margin-top:3px;font-family:var(--font-mono)}
.tr-tn{font-size:11px;color:var(--muted);margin-top:1px}
.tr-tile.flag{border-color:var(--danger)}
.tr-tile.flag .tr-tv,.tr-tile.flag .tr-tk{color:var(--danger)}

.tr-filters{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:2px 14px 13px;margin-bottom:14px}
.tr-srcbar{display:flex;gap:4px;border-bottom:1px solid var(--line);margin-bottom:11px;overflow-x:auto}
.tr-src{border:0;background:none;font:inherit;font-size:13px;color:var(--muted);padding:11px 12px 9px;cursor:pointer;white-space:nowrap;border-bottom:2px solid transparent;display:flex;align-items:center;gap:7px}
.tr-src:hover{color:var(--text)}
.tr-src.on{color:var(--accent);border-bottom-color:var(--accent);font-weight:600}
.tr-kind{font-size:9px;letter-spacing:.07em;text-transform:uppercase;border:1px solid currentColor;border-radius:4px;padding:0 4px;opacity:.7}
.tr-c{font-family:var(--font-mono);font-size:11px;background:var(--input);color:var(--muted);border-radius:20px;padding:1px 7px}
.tr-src.on .tr-c{color:var(--accent)}

.tr-row{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.tr-row+.tr-row{margin-top:9px}
.tr-lab{font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:var(--muted);font-weight:600}
.tr-pill{border:1px solid var(--border);background:var(--panel);color:var(--muted);font:inherit;font-size:12px;padding:5px 11px;border-radius:20px;cursor:pointer;display:inline-flex;gap:6px;align-items:center}
.tr-pill:hover{border-color:var(--accent);color:var(--text)}
.tr-pill.on{background:var(--accent);border-color:var(--accent);color:#fff;font-weight:600}
.tr-pill .c{font-family:var(--font-mono);font-size:11px;opacity:.7}
.tr-fld{border:1px solid var(--border);background:var(--input);color:var(--text);font:inherit;font-size:12px;padding:6px 10px;border-radius:8px}
.tr-chk{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--muted);cursor:pointer}

.tr-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(232px,1fr));gap:14px}
.tr-empty{grid-column:1/-1;padding:50px;text-align:center;color:var(--muted);border:1px dashed var(--border);border-radius:var(--radius)}
.tr-card{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);overflow:hidden;cursor:pointer;position:relative;display:flex;flex-direction:column;transition:border-color .12s,transform .12s}
.tr-card:hover{border-color:var(--accent);transform:translateY(-1px)}
.tr-card.sel{border-color:var(--accent);box-shadow:0 0 0 2px color-mix(in srgb,var(--accent) 30%,transparent)}
.tr-card.risk-ip::before,.tr-card.risk-nonpod::before{content:"";position:absolute;inset:0 0 auto;height:3px;z-index:3}
.tr-card.risk-ip::before{background:var(--danger)}
.tr-card.risk-nonpod::before{background:var(--warn)}

.tr-shot{position:relative;aspect-ratio:1/1;background:var(--input);overflow:hidden}
.tr-shot img{width:100%;height:100%;object-fit:cover;display:block}
.tr-noimg{width:100%;height:100%;background:var(--input)}
.tr-box{position:absolute;top:9px;left:9px;width:19px;height:19px;border-radius:5px;border:1.5px solid rgba(255,255,255,.9);background:rgba(15,20,35,.35);display:grid;place-items:center;color:#fff;font-size:12px;z-index:2}
.tr-card.sel .tr-box{background:var(--accent);border-color:var(--accent)}
.tr-heat{position:absolute;top:9px;right:9px;z-index:2;font-family:var(--font-mono);font-size:11px;font-weight:600;color:#fff;background:rgba(15,20,35,.6);border-radius:20px;padding:2px 8px}
.tr-heat.hi{background:var(--danger)}
.tr-fresh{position:absolute;top:9px;right:9px;z-index:2;font-size:10px;font-weight:700;color:#fff;background:var(--accent);border-radius:20px;padding:3px 8px}
.tr-rank{position:absolute;left:9px;bottom:9px;z-index:2;font-family:var(--font-mono);font-size:11px;font-weight:600;color:#fff;background:rgba(15,20,35,.6);border-radius:6px;padding:2px 7px}

.tr-body{padding:10px 12px 11px;display:flex;flex-direction:column;gap:7px;flex:1}
.tr-toprow{display:flex;align-items:center;gap:8px}
.tr-srctag{font-size:10px;font-weight:600;color:var(--info);border:1px solid var(--info);border-radius:5px;padding:1px 6px}
.tr-srctag.store{color:var(--warn);border-color:var(--warn)}
.tr-price{margin-left:auto;font-family:var(--font-mono);font-size:13px;font-weight:600}
.tr-title{font-size:12.5px;font-weight:600;line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.tr-title a{color:inherit;text-decoration:none}
.tr-title a:hover{color:var(--accent);text-decoration:underline}

.tr-mrow{display:flex;gap:6px}
.tr-m{flex:1;background:var(--input);border-radius:7px;padding:5px 7px;min-width:0}
.tr-mk{font-size:9px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);font-weight:600}
.tr-mv{font-family:var(--font-mono);font-size:13px;font-weight:600;margin-top:1px}
.tr-m.good .tr-mv{color:var(--ok)}
.tr-m.none{display:flex;align-items:center;justify-content:center;color:var(--muted);font-size:11px;text-align:center;padding:9px 7px}

.tr-derived{border:1px dashed var(--accent);border-radius:8px;padding:6px 8px;display:flex;gap:8px;align-items:center}
.tr-derived.plain{border-style:solid;border-color:var(--border);background:var(--input)}
.tr-d{flex:1;min-width:0}
.tr-dk{font-size:9px;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);font-weight:600}
.tr-dv{font-family:var(--font-mono);font-size:12.5px;font-weight:600}
.tr-dv small{font-size:10px;font-weight:500;color:var(--muted)}
.tr-dv.big{font-size:16px;color:var(--accent)}
.tr-dv.big.off{color:var(--muted)}
.tr-score{flex:none;text-align:center;padding-left:8px;border-left:1px solid var(--border)}

.tr-foot{display:flex;align-items:center;gap:6px;margin-top:auto;padding-top:2px}
.tr-shop{font-size:11px;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.tr-age{font-family:var(--font-mono);font-size:11px;color:var(--muted);margin-left:auto}
.tr-chips{display:flex;gap:5px;flex-wrap:wrap}
.tr-chip{font-size:10.5px;font-weight:600;border-radius:5px;padding:2px 7px;border:1px solid var(--border);color:var(--muted)}
.tr-chip.ok{color:var(--ok);border-color:var(--ok)}
.tr-chip.warn{color:var(--warn);border-color:var(--warn)}
.tr-chip.dang{color:var(--danger);border-color:var(--danger)}
.tr-chip.info{color:var(--info);border-color:var(--info)}

.tr-bar{position:sticky;bottom:0;background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:11px 16px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:14px;z-index:20}
.tr-n{font-size:13px;font-weight:600}
.tr-n span{font-family:var(--font-mono);color:var(--accent)}
.tr-note{font-size:12px;color:var(--muted);margin-left:auto}

.tr-lock{display:flex;justify-content:center;padding:60px 20px}
.tr-lockCard{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:28px 26px;width:340px;text-align:center}
.tr-lockIco{font-size:38px;line-height:1}.tr-lockCard h2{margin:8px 0 4px}
.tr-lockSub{color:var(--muted);font-size:13px;margin:0 0 16px}
.tr-lockInput{width:100%;background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:10px 12px;font-size:14px;text-align:center;letter-spacing:2px}
.tr-lockErr{color:var(--danger);font-size:12px;margin-top:10px}
`;
